import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config';
import './FinancialDashboard.css';
import Navbar from '../../shared/navbar.js';

// ─── Helpers ────────────────────────────────────────────────
const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n || 0);

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const daysOverdue = (dueDate) => {
  const diff = Math.floor((new Date() - new Date(dueDate + 'T00:00:00')) / 86400000);
  return diff;
};

const QUICK_RANGES = [
  { label: 'This Month', key: 'this_month' },
  { label: 'Last Month', key: 'last_month' },
  { label: 'This Quarter', key: 'this_quarter' },
  { label: 'YTD', key: 'ytd' },
  { label: 'Custom', key: 'custom' },
];

const TABS = [
  { key: 'overview', label: 'Overview', icon: '📊' },
  { key: 'rent-collection', label: 'Rent Collection', icon: '🏠' },
  { key: 'income', label: 'Income', icon: '📈' },
  { key: 'expenses', label: 'Expenses', icon: '📉' },
  { key: 'rent-roll', label: 'Rent Roll', icon: '📋' },
  { key: 'reports', label: 'Reports', icon: '📑' },
];

function getDateRange(key) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-based
  const pad = (n) => String(n).padStart(2, '0');
  switch (key) {
    case 'this_month':
      return { start: `${y}-${pad(m + 1)}-01`, end: now.toISOString().split('T')[0] };
    case 'last_month': {
      const lm = m === 0 ? 12 : m;
      const ly = m === 0 ? y - 1 : y;
      const last = new Date(ly, lm, 0).getDate();
      return { start: `${ly}-${pad(lm)}-01`, end: `${ly}-${pad(lm)}-${last}` };
    }
    case 'this_quarter': {
      const qStart = Math.floor(m / 3) * 3;
      return { start: `${y}-${pad(qStart + 1)}-01`, end: now.toISOString().split('T')[0] };
    }
    case 'ytd':
      return { start: `${y}-01-01`, end: now.toISOString().split('T')[0] };
    default:
      return { start: `${y}-${pad(m + 1)}-01`, end: now.toISOString().split('T')[0] };
  }
}

// ─── Mini Bar Chart ─────────────────────────────────────────
function TrendChart({ months }) {
  if (!months || months.length === 0) return <div className="chart-empty">No trend data yet</div>;
  const maxVal = Math.max(...months.map((m) => Math.max(m.income, m.expenses)), 1);
  return (
    <div className="trend-chart">
      {months.map((m) => (
        <div key={m.month} className="trend-bar-group">
          <div className="trend-bars">
            <div className="trend-bar income-bar" style={{ height: `${(m.income / maxVal) * 100}%` }} title={`Income: ${fmt(m.income)}`} />
            <div className="trend-bar expense-bar" style={{ height: `${(m.expenses / maxVal) * 100}%` }} title={`Expenses: ${fmt(m.expenses)}`} />
          </div>
          <div className="trend-label">{m.label.split(' ')[0]}</div>
        </div>
      ))}
      <div className="trend-legend">
        <span><span className="legend-dot income" />Income</span>
        <span><span className="legend-dot expense" />Expenses</span>
      </div>
    </div>
  );
}

// ─── Process Payment Modal (Stripe / Cash / Check / Manual) ──
function ProcessPaymentModal({ payment, onClose, onSuccess }) {
  const [mode, setMode] = useState('stripe_checkout'); // 'stripe_checkout' | 'cash' | 'check' | 'manual'
  const [form, setForm] = useState({
    paid_date: new Date().toISOString().split('T')[0],
    payment_method: 'check',
    amount: payment.amount || '',
  });
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!payment?.id) return;
    setProcessing(true);
    setError(null);

    try {
      if (mode === 'stripe_checkout') {
        // Redirect to the dedicated Stripe checkout page
        const returnPath = encodeURIComponent(window.location.pathname);
        window.location.href = `/checkout/${payment.id}?return=${returnPath}`;
        return; // page will redirect
      }

      if (mode === 'cash' || mode === 'check') {
        // Process cash/check via existing backend endpoint
        const res = await fetch(`${API_BASE_URL}/api/financial/payments/${payment.id}/process`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ payment_method: mode === 'cash' ? 'Cash' : 'Check' }),
        });
        const data = await res.json();
        if (data.error) {
          setError(data.error);
          setProcessing(false);
        } else {
          onSuccess();
        }
        return;
      }

      // Manual mark-paid
      const res = await fetch(`${API_BASE_URL}/api/financial/rental-payments/${payment.id}/mark-paid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) onSuccess();
      else {
        const d = await res.json().catch(() => ({}));
        setError(d.error || 'Failed to record payment');
      }
    } catch (e) {
      setError(e.message || 'An error occurred');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Process Payment</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p className="modal-sub">{payment.tenant_name} · {payment.property_name} · <strong>{fmt(payment.amount)}</strong></p>

          <label>Payment Method</label>
          <div className="payment-method-options">
            {[
              { key: 'stripe_checkout', label: 'Credit Card / Bank Transfer (Stripe)', icon: '💳' },
              { key: 'cash', label: 'Cash', icon: '💵' },
              { key: 'check', label: 'Check', icon: '🏦' },
              { key: 'manual', label: 'Record Manually', icon: '✏️' },
            ].map((opt) => (
              <label key={opt.key} className={`pmo-option ${mode === opt.key ? 'active' : ''}`}>
                <input type="radio" name="payMode" value={opt.key} checked={mode === opt.key} onChange={() => setMode(opt.key)} />
                <span className="pmo-icon">{opt.icon}</span>
                <span>{opt.label}</span>
              </label>
            ))}
          </div>

          {mode === 'stripe_checkout' && (
            <p className="modal-hint">You will be redirected to a secure Stripe checkout page to complete the payment.</p>
          )}

          {(mode === 'cash' || mode === 'check') && (
            <p className="modal-hint">This will immediately mark the payment as paid via {mode}.</p>
          )}

          {mode === 'manual' && (
            <div className="manual-fields">
              <label>Amount</label>
              <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              <label>Date Received</label>
              <input type="date" value={form.paid_date} onChange={(e) => setForm({ ...form, paid_date: e.target.value })} />
              <label>Method</label>
              <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
                <option value="check">Check</option>
                <option value="ach">ACH / Bank Transfer</option>
                <option value="cash">Cash</option>
                <option value="credit_card">Credit Card</option>
                <option value="zelle">Zelle</option>
                <option value="venmo">Venmo</option>
                <option value="money_order">Money Order</option>
                <option value="other">Other</option>
              </select>
            </div>
          )}

          {error && <div className="modal-error">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleSubmit} disabled={processing}>
            {processing
              ? 'Processing…'
              : mode === 'stripe_checkout'
              ? 'Go to Stripe Checkout'
              : mode === 'manual'
              ? 'Record Payment'
              : `Process ${mode === 'cash' ? 'Cash' : 'Check'} Payment`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Add / Edit Expense Modal ────────────────────────────────
function ExpenseModal({ expense, properties, onClose, onSuccess }) {
  const isEdit = !!expense?.id;
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState(
    expense || {
      property_id: '',
      expense_category: 'Maintenance',
      description: '',
      amount: '',
      expense_date: today,
      vendor_name: '',
      invoice_number: '',
      payment_status: 'unpaid',
      paid_date: '',
      notes: '',
    }
  );
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.amount || !form.expense_date) return;
    setSaving(true);
    try {
      const url = isEdit
        ? `${API_BASE_URL}/api/financial/expenses/${expense.id}`
        : `${API_BASE_URL}/api/financial/expenses`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) onSuccess();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? 'Edit Expense' : 'Add Expense'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body modal-grid2">
          <div>
            <label>Property</label>
            <select value={form.property_id || ''} onChange={(e) => setForm({ ...form, property_id: e.target.value || null })}>
              <option value="">— No property —</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>{p.property_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Category *</label>
            <select value={form.expense_category} onChange={(e) => setForm({ ...form, expense_category: e.target.value })}>
              {['Maintenance', 'Utilities', 'Insurance', 'Property Tax', 'Management Fee', 'Landscaping', 'Repairs', 'Legal', 'Marketing', 'Other'].map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label>Description</label>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What was this expense for?" />
          </div>
          <div>
            <label>Amount *</label>
            <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" />
          </div>
          <div>
            <label>Date *</label>
            <input type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} />
          </div>
          <div>
            <label>Vendor</label>
            <input value={form.vendor_name || ''} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} placeholder="Vendor name" />
          </div>
          <div>
            <label>Invoice #</label>
            <input value={form.invoice_number || ''} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} />
          </div>
          <div>
            <label>Status</label>
            <select value={form.payment_status} onChange={(e) => setForm({ ...form, payment_status: e.target.value })}>
              <option value="unpaid">Unpaid</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          {form.payment_status === 'paid' && (
            <div>
              <label>Date Paid</label>
              <input type="date" value={form.paid_date || ''} onChange={(e) => setForm({ ...form, paid_date: e.target.value })} />
            </div>
          )}
          <div className="col-span-2">
            <label>Notes</label>
            <textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Expense'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Add Other Income Modal ─────────────────────────────────
function OtherIncomeModal({ properties, onClose, onSuccess }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    property_id: '',
    category: 'parking',
    description: '',
    amount: '',
    income_date: today,
    payer_name: '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.amount || !form.income_date) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/financial/other-income`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) onSuccess();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add Other Income</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body modal-grid2">
          <div>
            <label>Property</label>
            <select value={form.property_id || ''} onChange={(e) => setForm({ ...form, property_id: e.target.value || null })}>
              <option value="">— No property —</option>
              {properties.map((p) => <option key={p.id} value={p.id}>{p.property_name}</option>)}
            </select>
          </div>
          <div>
            <label>Category *</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              <option value="parking">Parking</option>
              <option value="pet_fee">Pet Fee</option>
              <option value="amenity">Amenity</option>
              <option value="laundry">Laundry</option>
              <option value="storage">Storage</option>
              <option value="late_fee">Late Fee</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="col-span-2">
            <label>Description</label>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Details about this income" />
          </div>
          <div>
            <label>Amount *</label>
            <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" />
          </div>
          <div>
            <label>Date *</label>
            <input type="date" value={form.income_date} onChange={(e) => setForm({ ...form, income_date: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label>Payer Name</label>
            <input value={form.payer_name} onChange={(e) => setForm({ ...form, payer_name: e.target.value })} placeholder="Tenant name or payer" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Add Income'}</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════
const FinancialDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedProperty, setSelectedProperty] = useState('');
  const [rangeKey, setRangeKey] = useState('this_month');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);

  // Stripe return toast
  const [toast, setToast] = useState(null);

  // Banner
  const [overdueAlert, setOverdueAlert] = useState({ count: 0, total: 0 });

  // Tab data
  const [overviewData, setOverviewData] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [rentRollData, setRentRollData] = useState({ roll: [], property_summary: [] });
  const [incomeBreakdown, setIncomeBreakdown] = useState(null);
  const [otherIncomeList, setOtherIncomeList] = useState([]);
  const [expensesData, setExpensesData] = useState([]);
  const [plData, setPlData] = useState(null);
  const [cashFlowData, setCashFlowData] = useState(null);
  const [balanceSheetData, setBalanceSheetData] = useState(null);
  const [paymentsData, setPaymentsData] = useState([]);

  // Rent collection filters
  const [rentSection, setRentSection] = useState('all');
  const [incomeFilter, setIncomeFilter] = useState('all');
  const [expenseCategory, setExpenseCategory] = useState('all');
  const [reportType, setReportType] = useState('pl');

  // Modals
  const [processPaymentData, setProcessPaymentData] = useState(null);
  const [expenseModal, setExpenseModal] = useState(null); // null | 'new' | expense object
  const [showOtherIncomeModal, setShowOtherIncomeModal] = useState(false);

  const dateRange = rangeKey === 'custom' ? customRange : getDateRange(rangeKey);
  const apiParams = (extra = {}) => {
    const p = new URLSearchParams();
    if (selectedProperty) p.append('property_id', selectedProperty);
    if (dateRange.start) p.append('start_date', dateRange.start);
    if (dateRange.end) p.append('end_date', dateRange.end);
    Object.entries(extra).forEach(([k, v]) => { if (v) p.append(k, v); });
    return p.toString();
  };

  // ── Load properties + banner on mount ───────────────────
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/properties`)
      .then((r) => r.json())
      .then(setProperties)
      .catch(console.error);
  }, []);

  // ── Auto-dismiss toast ─────────────────────────────────
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Handle return from Stripe Checkout ─────────────────
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentId = urlParams.get('payment_id');
    const cancelled = urlParams.get('payment_cancelled');

    if (cancelled) {
      setToast({ type: 'warn', msg: 'Payment was cancelled.' });
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (sessionId && paymentId) {
      // Verify the Stripe session and update payment in DB
      fetch(`${API_BASE_URL}/api/stripe/checkout-session-status?session_id=${sessionId}&payment_id=${paymentId}`)
        .then((r) => r.json())
        .then((data) => {
          if (data.status === 'complete' || data.payment_status === 'paid') {
            setToast({ type: 'success', msg: `Payment #${paymentId} processed successfully via Stripe!` });
            // Switch to rent collection tab and refresh data
            setActiveTab('rent-collection');
          } else {
            setToast({ type: 'warn', msg: `Payment status: ${data.payment_status || data.status}. It may still be processing.` });
          }
          // Clean URL
          window.history.replaceState({}, document.title, window.location.pathname);
        })
        .catch((err) => {
          console.error('Error checking Stripe session:', err);
          window.history.replaceState({}, document.title, window.location.pathname);
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const p = new URLSearchParams();
    if (selectedProperty) p.append('property_id', selectedProperty);
    fetch(`${API_BASE_URL}/api/financial/rent-collection-summary?${p}`)
      .then((r) => r.json())
      .then((d) => setOverdueAlert({ count: d.overdue?.count || 0, total: d.overdue?.total || 0 }))
      .catch(console.error);
  }, [selectedProperty]);

  // ── Load tab data ────────────────────────────────────────
  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const [plRes, trendRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/financial/pl-statement?${apiParams()}`),
        fetch(`${API_BASE_URL}/api/financial/monthly-trend?${new URLSearchParams(selectedProperty ? { property_id: selectedProperty } : {})}`),
      ]);
      const pl = await plRes.json();
      const trend = await trendRes.json();
      setOverviewData(pl);
      setTrendData(Array.isArray(trend) ? trend : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedProperty, rangeKey, customRange]);

  const loadRentCollection = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/financial/rental-payments?${apiParams({ group_by: 'none' })}`);
      const data = await res.json();
      setPaymentsData(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedProperty, rangeKey, customRange]);

  const loadIncome = useCallback(async () => {
    setLoading(true);
    try {
      const [incRes, otherRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/financial/income-breakdown?${apiParams()}`),
        fetch(`${API_BASE_URL}/api/financial/other-income?${apiParams()}`),
      ]);
      setIncomeBreakdown(await incRes.json());
      setOtherIncomeList(await otherRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedProperty, rangeKey, customRange]);

  const loadExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/financial/expenses?${apiParams()}`);
      setExpensesData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedProperty, rangeKey, customRange]);

  const loadRentRoll = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (selectedProperty) p.append('property_id', selectedProperty);
      const res = await fetch(`${API_BASE_URL}/api/financial/rent-roll?${p}`);
      setRentRollData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [selectedProperty]);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      if (reportType === 'pl') {
        const res = await fetch(`${API_BASE_URL}/api/financial/pl-statement?${apiParams()}`);
        setPlData(await res.json());
      } else if (reportType === 'cash-flow') {
        const res = await fetch(`${API_BASE_URL}/api/financial/cash-flow?${apiParams()}`);
        setCashFlowData(await res.json());
      } else if (reportType === 'balance-sheet') {
        const res = await fetch(`${API_BASE_URL}/api/financial/balance-sheet?${apiParams({ as_of_date: dateRange.end })}`);
        setBalanceSheetData(await res.json());
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedProperty, rangeKey, customRange, reportType]);

  useEffect(() => {
    if (activeTab === 'overview') loadOverview();
    else if (activeTab === 'rent-collection') loadRentCollection();
    else if (activeTab === 'income') loadIncome();
    else if (activeTab === 'expenses') loadExpenses();
    else if (activeTab === 'rent-roll') loadRentRoll();
    else if (activeTab === 'reports') loadReports();
  }, [activeTab, selectedProperty, rangeKey, customRange, reportType]);

  // ── Rent Collection helpers ──────────────────────────────
  const today = new Date().toISOString().split('T')[0];
  const week = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];

  const categorise = (p) => {
    if (p.status === 'paid') return 'collected';
    if (p.due_date < today) return 'overdue';
    if (p.due_date <= week) return 'due_soon';
    return 'upcoming';
  };

  const rentSections = { overdue: [], due_soon: [], collected: [], upcoming: [] };
  paymentsData.forEach((p) => { rentSections[categorise(p)]?.push(p); });

  const filteredPayments =
    rentSection === 'all'
      ? paymentsData
      : (rentSection === 'overdue' ? rentSections.overdue
        : rentSection === 'due_soon' ? rentSections.due_soon
        : rentSection === 'collected' ? rentSections.collected
        : rentSections.upcoming);

  // ── Expenses helpers ─────────────────────────────────────
  const EXPENSE_CATS = ['All', 'Maintenance', 'Utilities', 'Insurance', 'Property Tax', 'Management Fee', 'Landscaping', 'Repairs', 'Legal', 'Marketing', 'Other'];
  const filteredExpenses = expenseCategory === 'all'
    ? expensesData
    : expensesData.filter((e) => e.expense_category === expenseCategory);

  const catTotals = {};
  expensesData.forEach((e) => {
    const cat = e.expense_category || 'Other';
    catTotals[cat] = (catTotals[cat] || 0) + (e.amount || 0);
  });

  // ── Income helpers ───────────────────────────────────────
  const incomeRows = (() => {
    if (!incomeBreakdown) return [];
    const rows = incomeBreakdown.rows || {};
    if (incomeFilter === 'all') return [...(rows.lease_rent || []), ...(rows.application_fees || []), ...(rows.deposits || []), ...(rows.other_income || [])];
    if (incomeFilter === 'lease_rent') return rows.lease_rent || [];
    if (incomeFilter === 'application_fees') return rows.application_fees || [];
    if (incomeFilter === 'deposits') return rows.deposits || [];
    return rows.other_income || [];
  })();

  // ── CSV Export ────────────────────────────────────────────
  const exportCSV = (data, filename) => {
    if (!data.length) return;
    const keys = Object.keys(data[0]);
    const csv = [keys.join(','), ...data.map((r) => keys.map((k) => JSON.stringify(r[k] ?? '')).join(','))].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = filename;
    a.click();
  };

  // ── Per-property overview rows ───────────────────────────
  const propOverviewRows = (rentRollData.property_summary || []).map((ps) => ({
    name: ps.property_name,
    total_units: ps.total_units,
    occupied: ps.occupied_units,
    scheduled_rent: ps.total_scheduled_rent,
  }));

  return (
    <div className="fd-page">
      <Navbar selected="Financial" />

      {/* ── Toast Notification ── */}
      {toast && (
        <div className={`fd-toast fd-toast-${toast.type}`} onClick={() => setToast(null)}>
          <span>{toast.type === 'success' ? '✓' : '⚠'}</span>
          <span>{toast.msg}</span>
          <button className="toast-close" onClick={() => setToast(null)}>✕</button>
        </div>
      )}

      {/* ── Overdue Alert Banner ── */}
      {overdueAlert.count > 0 && (
        <div className="fd-overdue-banner">
          <span>⚠ {overdueAlert.count} payment{overdueAlert.count !== 1 ? 's' : ''} overdue — {fmt(overdueAlert.total)} outstanding</span>
          <button className="banner-link" onClick={() => setActiveTab('rent-collection')}>View All →</button>
        </div>
      )}

      <div className="fd-workspace">
        {/* ── Sidebar ── */}
        <aside className="fd-sidebar">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`fd-sidebar-item${activeTab === t.key ? ' active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              <span className="fd-sidebar-icon">{t.icon}</span>
              <span className="fd-sidebar-label">{t.label}</span>
            </button>
          ))}
        </aside>

        {/* ── Main ── */}
        <div className="fd-main">
          {/* ── Top Controls ── */}
          <div className="fd-controls">
            <div className="fd-title-row">
              <h1>Financial Dashboard</h1>
            </div>
            <div className="fd-filters">
              <select className="fd-select" value={selectedProperty} onChange={(e) => setSelectedProperty(e.target.value)}>
                <option value="">All Properties</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>{p.property_name}</option>
                ))}
              </select>

              <div className="fd-range-pills">
                {QUICK_RANGES.map((r) => (
                  <button
                    key={r.key}
                    className={`range-pill ${rangeKey === r.key ? 'active' : ''}`}
                    onClick={() => setRangeKey(r.key)}
                  >
                    {r.label}
                  </button>
                ))}
              </div>

              {rangeKey === 'custom' && (
                <div className="fd-custom-range">
                  <input type="date" value={customRange.start} onChange={(e) => setCustomRange({ ...customRange, start: e.target.value })} />
                  <span>–</span>
                  <input type="date" value={customRange.end} onChange={(e) => setCustomRange({ ...customRange, end: e.target.value })} />
                </div>
              )}
            </div>
          </div>

          {/* ── Tab Content ── */}
          <div className="fd-content">
          {loading && (
            <div className="fd-loading">
              <div className="fd-spinner" />
              <p>Loading…</p>
            </div>
          )}

          {/* ════ OVERVIEW ════ */}
          {activeTab === 'overview' && !loading && (
            <div className="tab-overview">
              <div className="overview-kpi-groups">
                {/* Income */}
                <div className="kpi-group income-group">
                  <div className="kpi-group-header">Income</div>
                  <div className="kpi-row">
                    <div className="kpi-item">
                      <div className="kpi-label">Collected</div>
                      <div className="kpi-value positive">{fmt(overviewData?.revenue?.rental_income)}</div>
                    </div>
                  </div>
                  {overdueAlert.count > 0 && (
                    <div className="kpi-row">
                      <div className="kpi-item">
                        <div className="kpi-label overdue-label">Overdue</div>
                        <div className="kpi-value overdue-value">{fmt(overdueAlert.total)}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Expenses */}
                <div className="kpi-group expense-group">
                  <div className="kpi-group-header">Expenses</div>
                  <div className="kpi-row">
                    <div className="kpi-item">
                      <div className="kpi-label">Total Expenses</div>
                      <div className="kpi-value negative">{fmt(overviewData?.expenses?.total)}</div>
                    </div>
                  </div>
                </div>

                {/* Net */}
                <div className="kpi-group net-group">
                  <div className="kpi-group-header">Net</div>
                  <div className="kpi-row">
                    <div className="kpi-item">
                      <div className="kpi-label">Net Income</div>
                      <div className={`kpi-value ${(overviewData?.net_income || 0) >= 0 ? 'positive' : 'negative'}`}>
                        {fmt(overviewData?.net_income)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Expense by category breakdown */}
              {overviewData?.expenses?.by_category && Object.keys(overviewData.expenses.by_category).length > 0 && (
                <div className="overview-cat-breakdown">
                  <h3>Expense Breakdown</h3>
                  <div className="cat-breakdown-list">
                    {Object.entries(overviewData.expenses.by_category).map(([cat, amt]) => (
                      <div key={cat} className="cat-breakdown-row">
                        <span>{cat}</span>
                        <div className="cat-bar-wrap">
                          <div className="cat-bar" style={{ width: `${Math.min(100, (amt / (overviewData.expenses.total || 1)) * 100)}%` }} />
                        </div>
                        <span className="cat-amount">{fmt(amt)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Monthly trend chart */}
              <div className="overview-trend">
                <h3>6-Month Trend</h3>
                <TrendChart months={trendData} />
              </div>

              {/* Per-property summary */}
              {propOverviewRows.length > 0 && (
                <div className="overview-prop-table">
                  <h3>Per-Property Summary</h3>
                  <table className="fd-table">
                    <thead>
                      <tr>
                        <th>Property</th>
                        <th>Total Units</th>
                        <th>Occupied</th>
                        <th>Occupancy</th>
                        <th>Scheduled Rent / mo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {propOverviewRows.map((r, i) => (
                        <tr key={i}>
                          <td>{r.name}</td>
                          <td>{r.total_units}</td>
                          <td>{r.occupied}</td>
                          <td>{r.total_units ? `${Math.round((r.occupied / r.total_units) * 100)}%` : '—'}</td>
                          <td>{fmt(r.scheduled_rent)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ════ RENT COLLECTION ════ */}
          {activeTab === 'rent-collection' && !loading && (
            <div className="tab-rent-collection">
              {/* Status summary chips */}
              <div className="rc-summary-chips">
                <button className={`rc-chip overdue ${rentSection === 'overdue' ? 'active' : ''}`} onClick={() => setRentSection(rentSection === 'overdue' ? 'all' : 'overdue')}>
                  <span className="chip-count">{rentSections.overdue.length}</span>
                  <span>Overdue</span>
                  <span className="chip-amt">{fmt(rentSections.overdue.reduce((s, p) => s + p.amount, 0))}</span>
                </button>
                <button className={`rc-chip due-soon ${rentSection === 'due_soon' ? 'active' : ''}`} onClick={() => setRentSection(rentSection === 'due_soon' ? 'all' : 'due_soon')}>
                  <span className="chip-count">{rentSections.due_soon.length}</span>
                  <span>Due This Week</span>
                  <span className="chip-amt">{fmt(rentSections.due_soon.reduce((s, p) => s + p.amount, 0))}</span>
                </button>
                <button className={`rc-chip collected ${rentSection === 'collected' ? 'active' : ''}`} onClick={() => setRentSection(rentSection === 'collected' ? 'all' : 'collected')}>
                  <span className="chip-count">{rentSections.collected.length}</span>
                  <span>Collected</span>
                  <span className="chip-amt">{fmt(rentSections.collected.reduce((s, p) => s + p.amount, 0))}</span>
                </button>
                <button className={`rc-chip upcoming ${rentSection === 'upcoming' ? 'active' : ''}`} onClick={() => setRentSection(rentSection === 'upcoming' ? 'all' : 'upcoming')}>
                  <span className="chip-count">{rentSections.upcoming.length}</span>
                  <span>Upcoming</span>
                  <span className="chip-amt">{fmt(rentSections.upcoming.reduce((s, p) => s + p.amount, 0))}</span>
                </button>
              </div>

              <div className="fd-table-wrap">
                <table className="fd-table">
                  <thead>
                    <tr>
                      <th>Tenant</th>
                      <th>Property</th>
                      <th>Amount</th>
                      <th>Due Date</th>
                      <th>Status</th>
                      <th>Paid Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayments.length === 0 ? (
                      <tr><td colSpan={7} className="fd-empty">No payments in this category</td></tr>
                    ) : (
                      filteredPayments.map((p, i) => {
                        const cat = categorise(p);
                        const days = cat === 'overdue' ? daysOverdue(p.due_date) : null;
                        return (
                          <tr key={p.id || i} className={`rc-row rc-${cat}`}>
                            <td>{p.tenant_name || '—'}</td>
                            <td>{p.property_name || '—'}</td>
                            <td className="td-amount">{fmt(p.amount)}</td>
                            <td>
                              {fmtDate(p.due_date)}
                              {days !== null && <span className="days-overdue"> ({days}d overdue)</span>}
                            </td>
                            <td><span className={`status-pill st-${p.status}`}>{p.status}</span></td>
                            <td>{fmtDate(p.paid_date)}</td>
                            <td className="td-actions">
                              {p.status !== 'paid' && (
                                <button className="act-btn pay-btn" onClick={() => setProcessPaymentData(p)}>Process Payment</button>
                              )}
                              {p.tenant_email && (
                                <a className="act-btn remind-btn" href={`mailto:${p.tenant_email}?subject=Rent Reminder&body=Hi, this is a reminder that your rent of ${fmt(p.amount)} was due on ${fmtDate(p.due_date)}.`}>Remind</a>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ════ INCOME ════ */}
          {activeTab === 'income' && !loading && (
            <div className="tab-income">
              {/* Totals */}
              {incomeBreakdown && (
                <div className="income-totals">
                  {[
                    { key: 'all', label: 'All Income', val: incomeBreakdown.totals?.grand_total },
                    { key: 'lease_rent', label: 'Lease Rent', val: incomeBreakdown.totals?.lease_rent },
                    { key: 'application_fees', label: 'App Fees', val: incomeBreakdown.totals?.application_fees },
                    { key: 'deposits', label: 'Deposits', val: incomeBreakdown.totals?.deposits },
                    { key: 'other_income', label: 'Other Income', val: incomeBreakdown.totals?.other_income },
                  ].map((t) => (
                    <button
                      key={t.key}
                      className={`income-chip ${incomeFilter === t.key ? 'active' : ''}`}
                      onClick={() => setIncomeFilter(t.key)}
                    >
                      <span className="ic-label">{t.label}</span>
                      <span className="ic-val">{fmt(t.val)}</span>
                    </button>
                  ))}
                  <button className="btn-secondary add-income-btn" onClick={() => setShowOtherIncomeModal(true)}>+ Add Other Income</button>
                </div>
              )}

              <div className="fd-table-wrap">
                <table className="fd-table">
                  <thead>
                    <tr>
                      <th>Payer</th>
                      <th>Property</th>
                      <th>Category</th>
                      <th>Amount</th>
                      <th>Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incomeRows.length === 0 ? (
                      <tr><td colSpan={6} className="fd-empty">No income records in this category</td></tr>
                    ) : (
                      incomeRows.map((r, i) => (
                        <tr key={i}>
                          <td>{r.payer_name || '—'}</td>
                          <td>{r.property_name || '—'}</td>
                          <td><span className={`cat-badge cat-${(r.category || '').replace(/\s/g, '_')}`}>{r.category?.replace(/_/g, ' ')}</span></td>
                          <td className="td-amount">{fmt(r.amount)}</td>
                          <td>{fmtDate(r.income_date)}</td>
                          <td><span className={`status-pill st-${r.status}`}>{r.status}</span></td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ════ EXPENSES ════ */}
          {activeTab === 'expenses' && !loading && (
            <div className="tab-expenses">
              {/* Category summary cards */}
              {Object.keys(catTotals).length > 0 && (
                <div className="exp-cat-cards">
                  {Object.entries(catTotals).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([cat, amt]) => (
                    <div key={cat} className={`exp-cat-card ${expenseCategory === cat ? 'active' : ''}`} onClick={() => setExpenseCategory(expenseCategory === cat ? 'all' : cat)}>
                      <div className="ecc-label">{cat}</div>
                      <div className="ecc-val">{fmt(amt)}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Filter row */}
              <div className="exp-filter-row">
                <div className="exp-chips">
                  {EXPENSE_CATS.map((c) => (
                    <button
                      key={c}
                      className={`exp-chip ${expenseCategory === (c === 'All' ? 'all' : c) ? 'active' : ''}`}
                      onClick={() => setExpenseCategory(c === 'All' ? 'all' : c)}
                    >
                      {c}
                    </button>
                  ))}
                </div>
                <div className="exp-actions">
                  <button className="btn-secondary" onClick={() => exportCSV(filteredExpenses, 'expenses.csv')}>Export CSV</button>
                  <button className="btn-primary" onClick={() => setExpenseModal('new')}>+ Add Expense</button>
                </div>
              </div>

              <div className="fd-table-wrap">
                <table className="fd-table">
                  <thead>
                    <tr>
                      <th>Property</th>
                      <th>Category</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Date</th>
                      <th>Vendor</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredExpenses.length === 0 ? (
                      <tr><td colSpan={8} className="fd-empty">No expenses found</td></tr>
                    ) : (
                      filteredExpenses.map((e) => (
                        <tr key={e.id}>
                          <td>{e.property_name || '—'}</td>
                          <td><span className={`cat-badge cat-${(e.expense_category || '').replace(/\s/g, '_')}`}>{e.expense_category}</span></td>
                          <td>{e.description || '—'}</td>
                          <td className="td-amount">{fmt(e.amount)}</td>
                          <td>{fmtDate(e.expense_date)}</td>
                          <td>{e.vendor_name || '—'}</td>
                          <td><span className={`status-pill st-${e.payment_status}`}>{e.payment_status}</span></td>
                          <td className="td-actions">
                            <button className="act-btn edit-btn" onClick={() => setExpenseModal(e)}>Edit</button>
                            <button className="act-btn del-btn" onClick={async () => {
                              if (!window.confirm('Delete this expense?')) return;
                              await fetch(`${API_BASE_URL}/api/financial/expenses/${e.id}`, { method: 'DELETE' });
                              loadExpenses();
                            }}>Delete</button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ════ RENT ROLL ════ */}
          {activeTab === 'rent-roll' && !loading && (
            <div className="tab-rent-roll">
              {/* Per-property occupancy banners */}
              {(rentRollData.property_summary || []).map((ps) => (
                <div key={ps.property_id} className="rr-prop-banner">
                  <span className="rr-prop-name">{ps.property_name}</span>
                  <span className="rr-occ">{ps.occupied_units}/{ps.total_units} occupied ({ps.total_units ? Math.round((ps.occupied_units / ps.total_units) * 100) : 0}%)</span>
                  <span className="rr-rent">{fmt(ps.total_scheduled_rent)}/mo scheduled</span>
                </div>
              ))}

              <div className="rr-export-row">
                <button className="btn-secondary" onClick={() => exportCSV(rentRollData.roll || [], 'rent_roll.csv')}>Export CSV</button>
              </div>

              <div className="fd-table-wrap">
                <table className="fd-table">
                  <thead>
                    <tr>
                      <th>Property</th>
                      <th>Unit</th>
                      <th>Tenant</th>
                      <th>Monthly Rent</th>
                      <th>Status</th>
                      <th>Last Paid</th>
                      <th>Balance Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(rentRollData.roll || []).length === 0 ? (
                      <tr><td colSpan={7} className="fd-empty">No units found</td></tr>
                    ) : (
                      (rentRollData.roll || []).map((r, i) => {
                        const isVacant = !r.tenant_id;
                        const statusLabel = isVacant ? 'Vacant' : (r.balance_due > 0 ? 'Overdue' : r.last_payment_status === 'paid' ? 'Current' : (r.last_payment_status || 'Unknown'));
                        const statusClass = isVacant ? 'st-vacant' : r.balance_due > 0 ? 'st-overdue' : 'st-paid';
                        return (
                          <tr key={i} className={isVacant ? 'rr-vacant' : ''}>
                            <td>{r.property_name}</td>
                            <td>{r.unit_number}</td>
                            <td>{r.tenant_name || <span className="vacant-label">— Vacant —</span>}</td>
                            <td className="td-amount">{r.rent_amount ? fmt(r.rent_amount) : '—'}</td>
                            <td><span className={`status-pill ${statusClass}`}>{statusLabel}</span></td>
                            <td>{fmtDate(r.last_paid_date)}</td>
                            <td className={r.balance_due > 0 ? 'td-amount overdue-val' : 'td-amount'}>{r.balance_due ? fmt(r.balance_due) : '—'}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ════ REPORTS ════ */}
          {activeTab === 'reports' && !loading && (
            <div className="tab-reports">
              <div className="report-type-tabs">
                {[{ key: 'pl', label: 'P&L Statement' }, { key: 'cash-flow', label: 'Cash Flow' }, { key: 'balance-sheet', label: 'Balance Sheet' }].map((rt) => (
                  <button key={rt.key} className={`report-tab ${reportType === rt.key ? 'active' : ''}`} onClick={() => setReportType(rt.key)}>{rt.label}</button>
                ))}
              </div>

              {reportType === 'pl' && plData && (
                <div className="report-statement">
                  <div className="report-header">
                    <h2>Profit & Loss Statement</h2>
                    <p>{fmtDate(plData.period?.start_date)} – {fmtDate(plData.period?.end_date)}</p>
                  </div>
                  <div className="report-section">
                    <div className="rs-title">Revenue</div>
                    <div className="rs-line"><span>Rental Income</span><span>{fmt(plData.revenue?.rental_income)}</span></div>
                    <div className="rs-line rs-total"><span>Total Revenue</span><span>{fmt(plData.revenue?.total)}</span></div>
                  </div>
                  <div className="report-section">
                    <div className="rs-title">Expenses</div>
                    {plData.expenses?.by_category && Object.entries(plData.expenses.by_category).map(([cat, amt]) => (
                      <div key={cat} className="rs-line"><span>{cat}</span><span className="neg">{fmt(amt)}</span></div>
                    ))}
                    <div className="rs-line"><span>Maintenance</span><span className="neg">{fmt(plData.expenses?.maintenance)}</span></div>
                    <div className="rs-line"><span>Vendor Payables</span><span className="neg">{fmt(plData.expenses?.vendor_payables)}</span></div>
                    <div className="rs-line rs-total"><span>Total Expenses</span><span className="neg">{fmt(plData.expenses?.total)}</span></div>
                  </div>
                  <div className="report-section report-net">
                    <div className="rs-line rs-total"><span>Net Income</span><span className={(plData.net_income || 0) >= 0 ? 'pos' : 'neg'}>{fmt(plData.net_income)}</span></div>
                  </div>
                </div>
              )}

              {reportType === 'cash-flow' && cashFlowData && (
                <div className="report-statement">
                  <div className="report-header">
                    <h2>Cash Flow Statement</h2>
                    <p>{fmtDate(cashFlowData.period?.start_date)} – {fmtDate(cashFlowData.period?.end_date)}</p>
                  </div>
                  <div className="report-section">
                    <div className="rs-title">Operating Activities</div>
                    <div className="rs-line"><span>Cash from Rent</span><span className="pos">{fmt(cashFlowData.operating_activities?.cash_from_rental_payments)}</span></div>
                    <div className="rs-line"><span>Cash for Expenses</span><span className="neg">{fmt(cashFlowData.operating_activities?.cash_for_expenses)}</span></div>
                    <div className="rs-line"><span>Cash for Vendor Payables</span><span className="neg">{fmt(cashFlowData.operating_activities?.cash_for_vendor_payables)}</span></div>
                    <div className="rs-line rs-total"><span>Net Operating Cash</span><span className={(cashFlowData.operating_activities?.net_cash_operating || 0) >= 0 ? 'pos' : 'neg'}>{fmt(cashFlowData.operating_activities?.net_cash_operating)}</span></div>
                  </div>
                  <div className="report-section">
                    <div className="rs-title">Investing Activities</div>
                    <div className="rs-line"><span>Property Purchases</span><span className="neg">{fmt(cashFlowData.investing_activities?.cash_for_property_purchases)}</span></div>
                    <div className="rs-line rs-total"><span>Net Investing Cash</span><span className="neg">{fmt(cashFlowData.investing_activities?.net_cash_investing)}</span></div>
                  </div>
                  <div className="report-section report-net">
                    <div className="rs-line rs-total"><span>Net Change in Cash</span><span className={(cashFlowData.net_change_in_cash || 0) >= 0 ? 'pos' : 'neg'}>{fmt(cashFlowData.net_change_in_cash)}</span></div>
                  </div>
                </div>
              )}

              {reportType === 'balance-sheet' && balanceSheetData && (
                <div className="report-statement">
                  <div className="report-header">
                    <h2>Balance Sheet</h2>
                    <p>As of {fmtDate(balanceSheetData.as_of_date)}</p>
                  </div>
                  <div className="report-section">
                    <div className="rs-title">Assets</div>
                    <div className="rs-line"><span>Cash</span><span>{fmt(balanceSheetData.assets?.cash)}</span></div>
                    <div className="rs-line"><span>Accounts Receivable</span><span>{fmt(balanceSheetData.assets?.accounts_receivable)}</span></div>
                    <div className="rs-line"><span>Property Value</span><span>{fmt(balanceSheetData.assets?.property_value)}</span></div>
                    <div className="rs-line rs-total"><span>Total Assets</span><span>{fmt(balanceSheetData.assets?.total_assets)}</span></div>
                  </div>
                  <div className="report-section">
                    <div className="rs-title">Liabilities</div>
                    <div className="rs-line"><span>Accounts Payable</span><span className="neg">{fmt(balanceSheetData.liabilities?.accounts_payable)}</span></div>
                    <div className="rs-line rs-total"><span>Total Liabilities</span><span className="neg">{fmt(balanceSheetData.liabilities?.total_liabilities)}</span></div>
                  </div>
                  <div className="report-section report-net">
                    <div className="rs-line rs-total"><span>Total Equity</span><span className={(balanceSheetData.equity || 0) >= 0 ? 'pos' : 'neg'}>{fmt(balanceSheetData.equity)}</span></div>
                  </div>
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {processPaymentData && (
        <ProcessPaymentModal
          payment={processPaymentData}
          onClose={() => setProcessPaymentData(null)}
          onSuccess={() => {
            setProcessPaymentData(null);
            setToast({ type: 'success', msg: 'Payment recorded successfully!' });
            loadRentCollection();
            // Refresh overdue banner
            fetch(`${API_BASE_URL}/api/financial/rent-collection-summary${selectedProperty ? `?property_id=${selectedProperty}` : ''}`)
              .then((r) => r.json())
              .then((d) => setOverdueAlert({ count: d.overdue?.count || 0, total: d.overdue?.total || 0 }));
          }}
        />
      )}

      {expenseModal && (
        <ExpenseModal
          expense={expenseModal === 'new' ? null : expenseModal}
          properties={properties}
          onClose={() => setExpenseModal(null)}
          onSuccess={() => { setExpenseModal(null); loadExpenses(); }}
        />
      )}

      {showOtherIncomeModal && (
        <OtherIncomeModal
          properties={properties}
          onClose={() => setShowOtherIncomeModal(false)}
          onSuccess={() => { setShowOtherIncomeModal(false); loadIncome(); }}
        />
      )}
    </div>
  );
};

export default FinancialDashboard;
